import { Alert, Col, Modal, Row, Space, Switch, Table, Tooltip } from 'antd';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { TailSpin } from 'react-loader-spinner';
import { useToasts } from '../../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import IconButton from '../../../components/common/IconButton';
import AppSearch from '../../../components/common/AppSearch';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { cloneDeep } from 'lodash';
import { User } from '../../../models/User';
import { CustomBootstrapLinkType } from '../../../utils';


export default function UsersList() {
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState<User[]>();
  const [user, setUser] = useState<User>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line
  }, []);

  const filteredUsers = useMemo(() => users?.filter(u => u.username.toLowerCase().includes(searchText.toLowerCase())), [searchText, users]);
  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => setSearchText(evt.target.value);
  const reset = () => {
    setUsers(undefined);
    getUsers();
  }
  const openDeleteModal = (deleteUser: User) => () => {
    setUser(deleteUser);
    setShowDeleteModal(true);
  }

  const handleDeleteModalClose = () => {
    setUser(undefined);
    setShowDeleteModal(false);
  }

  const toggleUser = useCallback((user: User) => (enabled: boolean) => {
    const body = { ...user, enabled };

    ControlPanelManager.updateUser(body)
      .then(
        () => {
          const newUsers = cloneDeep(users!);
          const found = newUsers.findIndex(u => u.id === user.id);
          newUsers[found].enabled = enabled;
          setUsers(newUsers);
          showToast(`user-toggle-${enabled}`, `User ${user.username} ${enabled ? 'enabled' : 'disabled'}`, 'User Updated', 'success');
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [users, showToast]);

  const deleteUser = () => {
    showLoader();

    ControlPanelManager.deleteUser(user!.id!)
      .then(
        () => {
          hideLoader();
          showToast('user-success', 'User deleted successfully', 'User Success', 'success');
          reset();
          handleDeleteModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const getUsers = () => {
    ControlPanelManager.getUsers()
      .then(
        ({ data }) => setUsers(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderUsers = useCallback(() => (
    <Table size="middle" scroll={{ y: 'calc(100vh - 280px)' }} dataSource={filteredUsers} rowKey="id">
      <Table.Column
        key="admin"
        title=""
        width={40}
        className="text-center"
        render={(_, record: User) => record.attributes?.realmAdmin && (
          <Tooltip title="Admin">
            <FontAwesomeIcon className="ms-2 text-warning" icon={solid('crown')} />
          </Tooltip>
        )}
      />
      <Table.Column
        key="username"
        dataIndex="username"
        title="Username"
        render={(value, record: User) => <Link to={`/control-panel/users-management/${record.id}`}>{value}</Link>}
      />
      <Table.Column
        key="email"
        dataIndex="email"
        title="Email"
        render={(value, record: User) => (
          <>
            {(value && !record.emailVerified) && (
              <Tooltip title="Email not verified">
                <FontAwesomeIcon className="text-warning me-2" icon={solid('exclamation-circle')} />
              </Tooltip>
            )}
            <span className="text-muted">{value || '--'}</span>
          </>
        )}
      />
      <Table.Column
        key="actions"
        title="Actions"
        className="text-center"
        render={(_value, record: User) => (
          <Space size={1}>
            <div className={record.enabled ? 'text-primary' : 'text-muted'}>
              <Switch checked={record.enabled} onChange={toggleUser(record)} size="small" />
              <span className="ms-2">{record.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <Link to={`${record.id}/edit`} className="ms-3">
              <IconButton tooltip="Edit">
                <FontAwesomeIcon fixedWidth icon={solid('pen')} />
              </IconButton>
            </Link>
            <IconButton tooltip="Delete" className="text-danger ms-2" onClick={openDeleteModal(record)}>
              <FontAwesomeIcon fixedWidth icon={solid('trash')} />
            </IconButton>
          </Space>
        )}
      />
    </Table>
  ), [filteredUsers, toggleUser]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occured while retrieving users. Try again later."
      />
    )

    return filteredUsers
      ? renderUsers()
      : (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin
            height={100}
            width={100}
            color="#677ab1"
            radius="1"
          />
        </div>
      )
  }, [filteredUsers, error, renderUsers]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>User Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3" align="middle">
        <Col md={6}>
          <h5 className="mb-0">Users List</h5>
        </Col>

        <Col flex="1 0" className="text-center px-3">
          <AppSearch placeholder="Search user..." value={searchText} onChange={handleSearchChange} />
        </Col>

        <Col md={6} className="text-end">
          <Button size="sm" variant="success" as={Link as unknown as CustomBootstrapLinkType} to="/control-panel/users-management/create">
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register User</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal
        title="Delete User"
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        footer={
          <div className="d-flex justify-content-between mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteUser}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete user <b>{user?.username}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>
    </Container>
  )
}
